import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { RedshiftAiAccesses } from "./accesses.ts";
import { RedshiftAiComponents } from "./components.ts";
import { REDSHIFT_AI_INTEGRATION_KEY } from "./constants.ts";
import { RedshiftAiIconUrl } from "./icon.ts";

// Rule filter values compile to regular expressions. Every request carries
// every filter, empty where its access type has none, so a rule never
// matches by omission. `grant.entry` is the whole `PRIVILEGE:OBJECT` entry.
const RedshiftAiFilters = {
  cluster: { keys: ["id"] },
  role: { keys: ["name"] },
  object: { keys: ["name"] },
  privilege: { keys: ["name"] },
  grant: { keys: ["entry"] },
} as const satisfies AnyFilterSchema;
export type RedshiftAiFilters = typeof RedshiftAiFilters;

// Keyed by component, so a new component doesn't compile until it has one.
// The installer's "add an item" step asks for "the <identifier> of the
// <component> to install".
const IDENTIFIERS: Record<keyof typeof RedshiftAiComponents, string> = {
  cluster: "account/region/name",
  connector: "AWS account",
};
// `Registration` passes `identifier` a string that may name no component.
const identifiers: Partial<Record<string, string>> = IDENTIFIERS;

export const RedshiftAiRegistration = Registration({
  accesses: RedshiftAiAccesses,
  category: "resource",
  components: RedshiftAiComponents,
  docs: {},
  filters: RedshiftAiFilters,
  identifier: (component) => identifiers[component] ?? component,
  // Each component is named for what one of its items is.
  itemKind: (component) => component,
  label: "Redshift (AI)",
  logoUrl: RedshiftAiIconUrl,
  // No feature flag: the install is visible to every tenant once it merges,
  // with a Beta badge on each component.
  maturity: "beta",
  // The agent's request routes by the CLI's resource-type positional, so
  // `p0 request redshift-ai role …` only works if this is requestable.
  requestType: "requestable",
  // Every request acts on a cluster, so the integration is usable once one
  // is installed.
  useMapping: [{ component: "cluster", use: "iam_write" }],
});

registerIntegration(REDSHIFT_AI_INTEGRATION_KEY, RedshiftAiRegistration);
